import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import packagesData from "../data/packages";
import { MapPin, Clock, Star, Filter, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function PackagesPage() {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
  setPackages(packagesData);
  setLoading(false);
}, [category]);


  const filteredPackages = [...packages].sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    return 0;
  });

  const domesticPackages = filteredPackages.filter(p => p.category === 'domestic');
  const internationalPackages = filteredPackages.filter(p => p.category === 'international');

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="packages-page">
      {/* Hero */}
      <section className="relative h-64 md:h-80 bg-gradient-to-r from-teal-700 to-teal-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920"
            alt="Travel"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-medium text-white mb-4"
          >
            Tour Packages
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-teal-100"
          >
            Discover amazing destinations with our curated packages
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-500" />
            <span className="font-medium text-slate-700">Filter:</span>
            <div className="flex gap-2">
              {['all', 'domestic', 'international'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    category === cat
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Sort by:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Domestic Packages */}
      {(category === 'all' || category === 'domestic') && domesticPackages.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8" data-testid="domestic-section">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
            🇮🇳 Domestic Tour Packages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domesticPackages.map((pkg, index) => (
              <PackageCard key={pkg.id} pkg={pkg} index={index} />
            ))}
          </div>
        </section>
      )}

      {/* International Packages */}
      {(category === 'all' || category === 'international') && internationalPackages.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8 pb-20" data-testid="international-section">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
            🌍 International Tour Packages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {internationalPackages.map((pkg, index) => (
              <PackageCard key={pkg.id} pkg={pkg} index={index} />
            ))}
          </div>
        </section>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.12)] transition-all duration-300"
      data-testid={`package-card-${pkg.id}`}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={pkg.image}
          alt={pkg.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        {pkg.badge && (
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
            {pkg.badge}
          </span>
        )}
        <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-slate-700">
          {pkg.duration}
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">{pkg.name}</h3>
        <p className="text-sm text-slate-500 flex items-center gap-1 mb-3">
          <MapPin className="w-4 h-4" />
          {pkg.destination}
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {pkg.highlights?.slice(0, 3).map((h, i) => (
            <span key={i} className="px-2 py-1 bg-slate-100 rounded-md text-xs text-slate-600">
              {h}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div>
            <span className="text-sm text-slate-400 line-through">₹{pkg.original_price?.toLocaleString()}</span>
            <p className="text-2xl font-bold text-teal-700">₹{pkg.price?.toLocaleString()}</p>
          </div>
          <div className="flex gap-2">
            <Link to={`/packages/${pkg.id}`} data-testid={`view-details-${pkg.id}`}>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                View Details
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
